import { inject, injectable } from 'inversify';
import posthogClient, { type AutocaptureConfig, type SessionRecordingOptions } from 'posthog-js';
import { TrackingDependencyTypes } from '@shared/common/tracking/tracking.dependency-types';
import type { TrackingProviderConfig } from '@shared/common/tracking/domain/model/tracking-provider-config.model';
import type { TrackingService } from '@shared/common/tracking/domain/service/tracking.service';
import type { TrackingEventName } from '@shared/common/tracking/domain/model/tracking-event-name.model';
import type { TrackingProperties } from '@shared/common/tracking/domain/model/tracking-properties.model';
import type { InternalUserService } from '@shared/common/tracking/data/service/internal-user.service';
import { NO_CAPTURE_SELECTOR } from '@shared/common/tracking/domain/constant/no-capture.constant';
import type { TrackedUserTypes } from '@shared/common/tracking/domain/constant/tracked-user-types.constant';

@injectable()
export class PosthogTrackingService implements TrackingService {
  static InternalUserName = 'Kiplin';
  private isInitialized = false;

  public constructor(
    @inject(TrackingDependencyTypes.TrackingProviderConfig)
    private readonly config: TrackingProviderConfig,
    @inject(TrackingDependencyTypes.InternalUserService)
    private readonly internalUserService: InternalUserService,
  ) {}

  public initialize(): void {
    if (this.isInitialized || !this.config.apiKey || !this.config.apiHost) {
      return;
    }

    posthogClient.init(this.config.apiKey, {
      api_host: this.config.apiHost,
      ui_host: this.config.uiHost,
      defaults: '2026-01-30', // The defaults is a date, such as 2026-01-30, for a configuration snapshot used as defaults to initialize PostHog. This default is overridden when you explicitly set a value for any of the options. cf . https://posthog.com/docs/libraries/js
      autocapture: this.getAutocaptureConfiguration(),
      session_recording: this.getSessionRecordingConfiguration(),
      capture_dead_clicks: true,
    });

    this.isInitialized = true;
  }

  private getAutocaptureConfiguration(): AutocaptureConfig {
    // cf . https://posthog.com/docs/product-analytics/autocapture
    return {
      // Attribute filtering
      element_attribute_ignorelist: ['data-sensitive'],
      // Copy/cut capture
      capture_copied_text: true,
    };
  }

  private getSessionRecordingConfiguration(): SessionRecordingOptions {
    // cf . https://posthog.com/docs/session-replay/privacy?tab=Web
    return {
      maskAllInputs: false,
      maskInputOptions: {
        password: true,
      },
      maskTextSelector: NO_CAPTURE_SELECTOR,
    };
  }

  public track(eventName: TrackingEventName, properties?: TrackingProperties): void {
    if (!this.ensureInitialized()) {
      return;
    }

    posthogClient.capture(eventName, properties);
  }

  public identify(userId: string, userType: TrackedUserTypes): void {
    if (!this.ensureInitialized()) {
      return;
    }

    const loggedAs = this.internalUserService.isLoggedUserInternal();

    if (false === loggedAs) {
      posthogClient.identify(userId, {
        loggedAs,
        userType,
      });
    } else {
      posthogClient.identify(PosthogTrackingService.InternalUserName, {
        loggedAs,
        userType,
      });
    }
  }

  public clearIdentity(): void {
    if (!this.ensureInitialized()) {
      return;
    }

    posthogClient.reset();
  }

  public reportException(error: unknown): void {
    if (!this.ensureInitialized()) {
      return;
    }

    posthogClient.captureException(error);
  }

  public isFeatureEnabled(featureFlagKey: string): boolean {
    if (!this.ensureInitialized()) {
      // On renvoie true quand ce n'est pas initialisé pour toujours afficher les features dans les contextes de tests
      return true;
    }

    const isFeatureEnabled = posthogClient.isFeatureEnabled(featureFlagKey);

    return true === isFeatureEnabled;
  }

  public getFeatureFlag(featureFlagKey: string): boolean | string | undefined {
    if (!this.ensureInitialized()) {
      return undefined;
    }

    return posthogClient.getFeatureFlag(featureFlagKey);
  }

  private ensureInitialized(): boolean {
    this.initialize();

    return this.isInitialized;
  }
}
